import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import LanguageToggle from './components/LanguageToggle';
import LoginForm from './components/LoginForm';
import TrustBadges from './components/TrustBadges';
import SupabaseConnectionStatus from './components/SupabaseConnectionStatus';
import TestAccountsHealthCheck from './components/TestAccountsHealthCheck';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';

const Login = () => {
  const navigate = useNavigate();
  const { currentLanguage, switchLanguage, t } = useLanguage();
  const { signIn, signInDemo, loading: authLoading } = useAuth();
  const [error, setError] = useState('');
  // null = not yet determined, true = ready, false = critical failure
  const [diagnosticReady, setDiagnosticReady] = useState(null);
  const [networkError, setNetworkError] = useState(false);

  const handleLogin = async (credentials) => {
    try {
      setError('');
      setNetworkError(false);
      await signIn(credentials?.email, credentials?.password);
      navigate('/agency-dashboard');
    } catch (err) {
      console.error('Login error:', err);
      const rawMsg = (err?.message || '')?.toLowerCase();
      let errorMsg;
      if (
      rawMsg?.includes('load failed') ||
      rawMsg?.includes('failed to fetch') ||
      rawMsg?.includes('network') ||
      rawMsg?.includes('fetch') ||
      rawMsg?.includes('timeout'))
      {
        setNetworkError(true);
        errorMsg = currentLanguage === 'fr' ?'Impossible de joindre le serveur d\'authentification. Vérifiez votre connexion ou utilisez le mode démo ci-dessous.'
          : 'تعذّر الاتصال بخادم المصادقة. تحقق من اتصالك أو استخدم وضع العرض التجريبي أدناه.';
      } else if (
      rawMsg?.includes('invalid login') ||
      rawMsg?.includes('invalid credentials') ||
      rawMsg?.includes('email not confirmed') ||
      rawMsg?.includes('wrong password'))
      {
        errorMsg = currentLanguage === 'fr' ? 'Email ou mot de passe incorrect.' : 'البريد الإلكتروني أو كلمة المرور غير صحيحة.';
      } else {
        errorMsg = currentLanguage === 'fr' ?
        `Erreur de connexion: ${err?.message || 'Veuillez réessayer.'}` :
        `خطأ في تسجيل الدخول: ${err?.message || 'يرجى المحاولة مرة أخرى.'}`;
      }
      setError(errorMsg);
    }
  };

  const handleDemoAccess = () => {
    signInDemo();
    navigate('/agency-dashboard');
  };

  const handleLanguageChange = (newLanguage) => {
    switchLanguage(newLanguage);
  };

  const content = {
    fr: {
      welcomeTitle: 'Bienvenue sur OmraHajjManager',
      welcomeSubtitle: 'Plateforme de gestion complète pour les agences de voyage Omra & Hajj',
      features: [
      'Gestion multi-agences sécurisée',
      'Suivi complet des pèlerins',
      'Intégration Nusuk officielle',
      'Conformité réglementaire saoudienne'],

      footerText: 'Plateforme certifiée pour la gestion des pèlerinages Omra & Hajj',
      copyright: `© ${new Date()?.getFullYear()} OmraHajjManager. Tous droits réservés.`
    },
    ar: {
      welcomeTitle: 'مرحباً بك في OmraHajjManager',
      welcomeSubtitle: 'منصة إدارة شاملة لوكالات السفر للعمرة والحج',
      features: [
      'إدارة آمنة متعددة الوكالات',
      'متابعة كاملة للحجاج',
      'تكامل نسك الرسمي',
      'الامتثال التنظيمي السعودي'],

      footerText: 'منصة معتمدة لإدارة رحلات العمرة والحج',
      copyright: `© ${new Date()?.getFullYear()} OmraHajjManager. جميع الحقوق محفوظة.`
    }
  };

  const pageContent = content?.[currentLanguage];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="absolute top-4 right-4 md:top-6 md:right-6 lg:top-8 lg:right-8 z-10">
        <LanguageToggle
          currentLanguage={currentLanguage}
          onLanguageChange={handleLanguageChange} />
        
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-8 md:py-12">
        <div className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          <div className="hidden lg:flex flex-col justify-center space-y-6 md:space-y-8">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center">
                <Icon name="Compass" size={32} className="text-primary" />
              </div>
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground">
                  OmraHajjManager
                </h2>
                <p className="caption text-muted-foreground">
                  {pageContent?.welcomeSubtitle}
                </p>
              </div>
            </div>

            <div className="relative rounded-xl overflow-hidden shadow-warm-lg">
              <Image
                src="https://images.unsplash.com/photo-1713395075973-e271267dcd2f"
                alt="Aerial view of Masjid al-Haram in Mecca with thousands of pilgrims performing Tawaf around the Kaaba during Hajj season at sunset with golden lighting"
                className="w-full h-96 object-cover" />
              
              <div className="absolute inset-0 bg-gradient-to-t from-background/90 to-transparent" />
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-foreground">
                {pageContent?.welcomeTitle}
              </h3>
              <ul className="space-y-3">
                {pageContent?.features?.map((feature, index) =>
                <li key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-success/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Icon name="Check" size={14} className="text-success" />
                    </div>
                    <span className="text-sm md:text-base text-foreground">
                      {feature}
                    </span>
                  </li>
                )}
              </ul>
            </div>
          </div>

          <div className="flex flex-col justify-center">
            <div className="bg-card rounded-xl shadow-warm-lg p-6 md:p-8 lg:p-10 border border-border">
              <div className="lg:hidden flex items-center justify-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Icon name="Compass" size={24} className="text-primary" />
                </div>
                <span className="text-xl font-bold text-foreground">
                  OmraHajjManager
                </span>
              </div>

              <LoginForm currentLanguage={currentLanguage} onSubmit={handleLogin} externalError={error} networkError={networkError} />

              {/* Test account credentials */}
              <div className="mt-4 p-4 rounded-lg bg-muted/50 border border-border space-y-3">
                <p className="font-semibold text-foreground text-sm flex items-center gap-2">
                  <span>🔑</span>
                  {currentLanguage === 'fr' ? 'Comptes de test disponibles' : 'حسابات الاختبار المتاحة'}
                </p>
                <div className="space-y-2">
                  <div className="p-2 rounded bg-background border border-border">
                    <p className="text-xs font-medium text-primary mb-1">
                      {currentLanguage === 'fr' ? '👑 Administrateur' : '👑 المسؤول'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Email: <span className="font-mono text-foreground select-all">admin@test.omramanager.com</span>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {currentLanguage === 'fr' ? 'Mot de passe' : 'كلمة المرور'}: <span className="font-mono text-foreground select-all">Admin@2026!</span>
                    </p>
                  </div>
                  <div className="p-2 rounded bg-background border border-border">
                    <p className="text-xs font-medium text-success mb-1">
                      {currentLanguage === 'fr' ? '🏢 Agence de voyage' : '🏢 وكالة السفر'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Email: <span className="font-mono text-foreground select-all">agency@test.omramanager.com</span>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {currentLanguage === 'fr' ? 'Mot de passe' : 'كلمة المرور'}: <span className="font-mono text-foreground select-all">Agency@2026!</span>
                    </p>
                  </div>
                  <div className="p-2 rounded bg-background border border-border">
                    <p className="text-xs font-medium text-warning mb-1">
                      {currentLanguage === 'fr' ? '📊 Compte démo (données réelles)' : '📊 حساب تجريبي (بيانات حقيقية)'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Email: <span className="font-mono text-foreground select-all">admin@omramanager.ma</span>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {currentLanguage === 'fr' ? 'Mot de passe' : 'كلمة المرور'}: <span className="font-mono text-foreground select-all">Admin2026!</span>
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <button
                  onClick={handleDemoAccess}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-primary/30 bg-primary/5 hover:bg-primary/10 text-primary font-semibold text-sm transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
                  {currentLanguage === 'fr' ? 'Accéder au tableau de bord (Démo)' : 'الدخول إلى لوحة التحكم (تجريبي)'}
                </button>
              </div>

              <SupabaseConnectionStatus
                currentLanguage={currentLanguage}
                onDiagnosticComplete={(isReady) => setDiagnosticReady(isReady)}
              />

              <TestAccountsHealthCheck currentLanguage={currentLanguage} />
            </div>

            <div className="mt-8 md:mt-10">
              <TrustBadges currentLanguage={currentLanguage} />
            </div>
          </div>
        </div>
      </div>

      <footer className="border-t border-border bg-card/50 py-6 md:py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center space-y-2">
            <p className="text-sm text-muted-foreground">
              {pageContent?.footerText}
            </p>
            <p className="caption text-muted-foreground">
              {pageContent?.copyright}
            </p>
          </div>
        </div>
      </footer>
    </div>);

};

export default Login;